import { PDFDocument, PDFFont, PDFPage, rgb, StandardFonts } from 'pdf-lib';
import { normalizeForPdf, wrapText } from './pdf-helper';

export type DealerInventoryPdfTable = {
	title: string;
	columns: string[];
	rows: string[][];
};

type BuildPdfArgs = {
	customerNumber: string;
	customerName: string;
	summaryLines: { label: string; value: string }[];
	tables: DealerInventoryPdfTable[];
};

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const MARGIN = 40;
const FOOTER_MARGIN = 48;

function addPage(pdfDoc: PDFDocument): PDFPage {
	return pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
}

function cellText(value: unknown): string {
	if (value == null) return '';
	if (typeof value === 'boolean') return value ? 'Yes' : 'No';
	return String(value);
}

export async function buildDealerInventoryPdfBytes(args: BuildPdfArgs): Promise<Uint8Array> {
	const pdfDoc = await PDFDocument.create();
	const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
	const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

	let page = addPage(pdfDoc);
	let y = PAGE_HEIGHT - MARGIN;

	const ensureSpace = (needed: number) => {
		if (y - needed < FOOTER_MARGIN) {
			page = addPage(pdfDoc);
			y = PAGE_HEIGHT - MARGIN;
		}
	};

	const drawLine = (text: string, size: number, f: PDFFont) => {
		const lines = wrapText(normalizeForPdf(text), f, size, PAGE_WIDTH - MARGIN * 2);
		for (const line of lines) {
			ensureSpace(14);
			page.drawText(line, {
				x: MARGIN,
				y,
				size,
				font: f,
				color: rgb(0, 0, 0),
			});
			y -= size + 2;
		}
	};

	drawLine('Dealer inventory', 16, fontBold);
	y -= 4;
	drawLine(`${args.customerName} (${args.customerNumber})`, 11, font);
	y -= 8;

	for (const line of args.summaryLines) {
		drawLine(`${line.label}: ${line.value}`, 10, font);
	}
	y -= 10;

	for (const table of args.tables) {
		drawLine(table.title, 12, fontBold);
		y -= 4;

		if (table.columns.length === 0) {
			drawLine('(No columns)', 9, font);
			y -= 6;
			continue;
		}

		const colCount = table.columns.length;
		const colWidth = (PAGE_WIDTH - MARGIN * 2) / colCount;
		const headerSize = 8;
		const bodySize = 7;

		const truncate = (t: string, maxW: number, f: PDFFont, size: number) => {
			let s = normalizeForPdf(t);
			while (s.length > 0 && f.widthOfTextAtSize(s, size) > maxW - 4) {
				s = s.slice(0, -2);
			}
			if (s.length < normalizeForPdf(t).length) s = `${s}…`;
			return s;
		};

		ensureSpace(20);
		let x = MARGIN;
		for (const col of table.columns) {
			const label = truncate(col, colWidth, fontBold, headerSize);
			page.drawText(label, { x: x + 2, y, size: headerSize, font: fontBold, color: rgb(0, 0, 0) });
			x += colWidth;
		}
		y -= headerSize + 6;

		for (const row of table.rows) {
			ensureSpace(bodySize + 8);
			x = MARGIN;
			for (let i = 0; i < colCount; i++) {
				const raw = row[i] ?? '';
				const txt = truncate(cellText(raw), colWidth, font, bodySize);
				page.drawText(txt, { x: x + 2, y, size: bodySize, font, color: rgb(0.1, 0.1, 0.1) });
				x += colWidth;
			}
			y -= bodySize + 4;
		}

		y -= 12;
	}

	const bytes = await pdfDoc.save();
	return bytes;
}

export function downloadDealerInventoryPdf(filename: string, bytes: Uint8Array) {
	const blob = new Blob([bytes], { type: 'application/pdf' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	a.click();
	URL.revokeObjectURL(url);
}
